import { OrgUnitLocation, StoreHours, OrgUnitsDataActions } from '@msdyn365-commerce/retail-proxy';
import { IHeiStoreHoursData } from './hei-store-hours.data';
import { IHeiStoreHoursProps, IHeiStoreHoursResources } from './hei-store-hours.props.autogenerated';
import { IStoreSelectorLocationLineItemResources } from '../store-selector/components/store-selector-location-line-item';
import { useEffect, useState } from 'react';

export type IHeiStoreHoursViewProps = IHeiStoreHoursProps<IHeiStoreHoursData> & {
    storeInfo: {
        storeHours: StoreHours | undefined;
        location: OrgUnitLocation;
        resources: IStoreSelectorLocationLineItemResources;
    };
};

const HeiStoreHours = (props: IHeiStoreHoursProps<IHeiStoreHoursData>) => {
    const {
        resources,
        context,
        config: { orgUnitNumber }
    } = props;

    const [storeHours, setStoreHours] = useState<StoreHours>();
    const [orgUnitLocation, setOrgUnitLocation] = useState<OrgUnitLocation>();
    const [storeHoursLoaded, setStoreHoursLoaded] = useState(false);

    useEffect(() => {
        const ctx = { callerContext: context.actionContext };

        if (storeHoursLoaded) {
            return;
        }

        setStoreHoursLoaded(true);

        void (async () => {
            try {
                const hours = await OrgUnitsDataActions.getStoreHoursAsync(ctx, orgUnitNumber);
                const allStores = await OrgUnitsDataActions.searchOrgUnitLocationsAsync(ctx, {
                    OrgUnitNumber: orgUnitNumber
                });

                const store = allStores?.find(s => s.OrgUnitNumber === orgUnitNumber);

                if (hours && store) {
                    setStoreHours(hours);
                    setOrgUnitLocation(store);
                }
            } catch (error) {
                context.telemetry.exception(error as Error);
            }
        })();
    }, [orgUnitNumber]);

    if (!storeHours || !orgUnitLocation) {
        return null;
    }

    const viewProps: IHeiStoreHoursViewProps = {
        ...props,
        storeInfo: {
            storeHours: storeHours,
            location: orgUnitLocation,
            resources: mapResources(resources)
        }
    };

    return props.renderView(viewProps);
};

const mapResources = (resources: IHeiStoreHoursResources): IStoreSelectorLocationLineItemResources => {
    return {
        contactInfoHeader: resources.contactText,
        storeHoursHeader: resources.timeText,
        availabilityInfoHeader: resources.availabilityText,

        closedText: resources.storeHoursClosedText,
        outOfStock: resources.outOfStockText,
        inStock: resources.inStockText,

        currentLocation: resources.selectedStoreLocationText,
        selectLocation: resources.selectStoreText,
        selectLocationAriaLabelFormat: resources.selectStoreAriaFormatText,
        setAsPreferredStoreText: resources.setAsPreferredStoreText,
        setAsPreferredStoreTextAriaLabel: resources.setAsPreferredStoreTextAriaLabel,
        preferredStoreText: resources.preferredStoreText,
        preferredStoreAriaLabel: resources.preferredStoreAriaLabel,

        days: {
            monday: resources.storeHoursMondayText,
            tuesday: resources.storeHoursTuesdayText,
            wednesday: resources.storeHoursWednesdayText,
            thursday: resources.storeHoursThursdayText,
            friday: resources.storeHoursFridayText,
            saturday: resources.storeHoursSaturdayText,
            sunday: resources.storeHoursSundayText,
            mondayFull: resources.storeHoursMondayFullText,
            tuesdayFull: resources.storeHoursTuesdayFullText,
            wednesdayFull: resources.storeHoursWednesdayFullText,
            thursdayFull: resources.storeHoursThursdayFullText,
            fridayFull: resources.storeHoursFridayFullText,
            saturdayFull: resources.storeHoursSaturdayFullText,
            sundayFull: resources.storeHoursSundayFullText
        }
    };
};

export default HeiStoreHours;
